// Lógica pura do painel "Horas da equipe" — sem React, sem antd, sem rede
// (o jest do CRA não carrega o antd v6; aqui dá para testar de verdade).
//
// A RÉGUA (quem deve hora, quanto falta, dia completo ≥ 90% da carga, pessoa em
// dia com ≥ 90% do esperado no período) NÃO mora aqui: vem pronta do servidor (dre-engine/handlers/cargaHoras.js), para a tela
// nunca dizer "em dia" de quem o robô de cobrança vai cobrar. Aqui só se
// escolhe o período, se filtra, se ordena e se escreve a frase para gente.
import { diaDaSemana, formatarHoras, somarDias } from '../operacional-horas/horasUtils';

export type EstadoDia =
  | 'completo' | 'incompleto' | 'vazio'   // dia devido, já encerrado
  | 'hoje'                                // devido, ainda acontecendo (não é cobrado)
  | 'ausencia'                            // férias, atestado, folga…
  | 'extra'                               // hora em dia que não era devido (fim de semana, feriado)
  | 'presumido'                           // dia útil de quem tem rateio fixo no cadastro: não deve anotação
  | 'fora' | 'futuro';

export type DiaPainel = {
  data: string;
  estado: EstadoDia;
  minutos: number;
  falta: number;
  remoto: boolean;
  campo: boolean;
  tipo_ausencia: string | null;
  /** Minutos do dia que vieram do CLOCKIFY (dia sem nada no anotador novo). 0 = nada veio de lá. */
  clockify?: number;
  /** Minutos PRESUMIDOS pelo rateio fixo da pessoa (a carga do dia menos o que ela anotou). */
  presumido?: number;
};

export type PessoaPainel = {
  profissional_id: string | null;
  nome: string;
  usuario_email: string;
  setor: string | null;
  chefe_nome: string | null;
  sem_ficha: boolean;
  /** Rateio fixo no cadastro (o mesmo da DRE): a pessoa NÃO deve anotação. null = anota normalmente. */
  rateio_fixo?: Array<{ rotulo: string; percentual: number }> | null;
  minutos_presumidos?: number;
  dias_presumidos?: number;
  horas_dia: number;
  minutos: number;
  minutos_ate_ontem: number;
  minutos_hoje: number;
  minutos_contrato: number;
  /** Quanto do total da pessoa veio do Clockify, e em quantos dias. */
  minutos_clockify?: number;
  dias_clockify?: number;
  carga_min: number;
  /** Soma do que faltou DIA A DIA (sem compensação) — é o que o lembrete do robô olha. */
  falta_min: number;
  cobertura_pct: number | null;
  /** Veredito do período: anotou ≥ 90% do esperado. null = nada era esperado. */
  em_dia: boolean | null;
  /** Quanto falta para fechar o esperado do período (hora a mais num dia cobre a menos em outro). */
  devendo_min: number;
  dias_devidos: number;
  dias_completos: number;
  dias_incompletos: number;
  dias_ausencia: number;
  dias_remoto: number;
  dias_campo: number;
  mais_antigo: string | null;
  dias_uteis_em_atraso: number;
  ultimo_dia: string | null;
  dias: DiaPainel[];
};

export type ResumoPainel = {
  pessoas: number;
  minutos: number;
  minutos_ate_ontem: number;
  minutos_hoje: number;
  minutos_contrato: number;
  /** Quanto do anotado veio do Clockify (dias sem nada no anotador novo). */
  minutos_clockify: number;
  /** Horas presumidas de quem tem rateio fixo, e quantas pessoas são. */
  minutos_presumidos: number;
  pessoas_rateio_fixo: number;
  carga_min: number;
  /** Anotado até ontem por quem tinha hora esperada — o numerador de `cobertura_pct`. */
  minutos_cobertos: number;
  falta_min: number;
  /** Quanto falta no total, só de quem não está em dia. */
  devendo_min: number;
  cobertura_pct: number | null;
  pessoas_com_carga: number;
  pessoas_em_dia: number;
  pessoas_com_falta: number;
  pessoas_em_atraso: number;
  pessoas_sem_ficha: number;
};

export type DestinoPainel = {
  /** Identidade da linha: contrato, centro de custo ou projeto do Clockify. */
  chave?: string;
  contrato_id: string | null;
  centro_custo_codigo: string | null;
  rotulo: string;
  detalhe: string;
  minutos: number;
  pessoas: number;
  /** Quanto cada pessoa pôs neste destino — a tela soma só quem está no filtro. */
  por_pessoa: Array<{ usuario_email: string; minutos: number }>;
};

export type Painel = {
  inicio: string;
  fim: string;
  hoje: string;
  pct_dia_completo: number;
  /** false = o servidor não leu o Clockify (desligado ou cópia ausente). */
  clockify_lido?: boolean;
  resumo: ResumoPainel;
  pessoas: PessoaPainel[];
  destinos: DestinoPainel[];
};

// ── Período ────────────────────────────────────────────────────────────────
export type ChavePeriodo = '7d' | '14d' | 'mes' | 'mes_passado';
export const PERIODOS: Array<{ value: ChavePeriodo; label: string }> = [
  { value: '7d', label: '7 dias' },
  { value: '14d', label: '14 dias' },
  { value: 'mes', label: 'Este mês' },
  { value: 'mes_passado', label: 'Mês passado' },
];
/** O servidor recusa mais que isto (um trimestre). */
export const MAX_DIAS = 93;

/** Intervalo de um atalho, a partir do "hoje" ('YYYY-MM-DD').
 *  Os atalhos "N dias" e "Este mês" terminam em hoje: dia que ainda não chegou
 *  não tem o que mostrar. Tudo por texto — sem fuso do computador no meio. */
export function periodoDe(chave: ChavePeriodo, hoje: string): { inicio: string; fim: string } {
  const [y, m] = hoje.split('-').map(Number);
  const p2 = (n: number) => String(n).padStart(2, '0');
  if (chave === '7d') return { inicio: somarDias(hoje, -6), fim: hoje };
  if (chave === '14d') return { inicio: somarDias(hoje, -13), fim: hoje };
  if (chave === 'mes') return { inicio: `${y}-${p2(m)}-01`, fim: hoje };
  const ano = m === 1 ? y - 1 : y;
  const mes = m === 1 ? 12 : m - 1;
  return { inicio: `${ano}-${p2(mes)}-01`, fim: somarDias(`${y}-${p2(m)}-01`, -1) };
}

/** Quantos dias tem o intervalo (as duas pontas contam). */
export function diasNoIntervalo(inicio: string, fim: string): number {
  const t = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  return Math.round((t(fim) - t(inicio)) / 86400000) + 1;
}

/** Frase de recusa de um intervalo escolhido à mão, ou null se serve. */
export function erroDoIntervalo(inicio: string, fim: string): string | null {
  if (fim < inicio) return 'O fim do período tem que ser depois do início.';
  if (diasNoIntervalo(inicio, fim) > MAX_DIAS) return `Escolha um período de até ${MAX_DIAS} dias (um trimestre).`;
  return null;
}

// ── Datas para gente ───────────────────────────────────────────────────────
/** '2026-09-30' → '30/09'. */
export const dataCurta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/** '2026-09-30' → 'qua 30/09'. */
export const diaCurto = (iso: string) => `${diaDaSemana(iso).toLowerCase()} ${dataCurta(iso)}`;

/** Percentual à brasileira: 86.6 → '86,6%'; sem valor → '—'. */
export const pctBR = (pct: number | null | undefined) =>
  (pct === null || pct === undefined ? '—' : `${String(pct).replace('.', ',')}%`);

// ── Cores ──────────────────────────────────────────────────────────────────
export const corCobertura = (pct: number | null) =>
  pct === null ? '#bfbfbf' : pct >= 90 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f';

/** Cor do quadradinho de cada dia. `borda` existe para os estados "vazios"
 *  (não devia hora / ainda vai chegar), que são desenhados só com contorno. */
export function corDoEstado(estado: EstadoDia): { fundo: string; borda: string } {
  switch (estado) {
    case 'completo': return { fundo: '#52c41a', borda: '#52c41a' };
    case 'incompleto': return { fundo: '#faad14', borda: '#faad14' };
    case 'vazio': return { fundo: '#ff4d4f', borda: '#ff4d4f' };
    case 'hoje': return { fundo: '#bae0ff', borda: '#1677ff' };
    case 'ausencia': return { fundo: '#d3adf7', borda: '#d3adf7' };
    case 'extra': return { fundo: '#87e8de', borda: '#87e8de' };
    case 'presumido': return { fundo: '#d6e4ff', borda: '#adc6ff' };
    case 'futuro': return { fundo: 'transparent', borda: '#f0f0f0' };
    default: return { fundo: '#f5f5f5', borda: '#f0f0f0' };
  }
}

export const LEGENDA: Array<{ estado: EstadoDia; rotulo: string }> = [
  { estado: 'completo', rotulo: 'dia completo' },
  { estado: 'incompleto', rotulo: 'dia com menos hora' },
  { estado: 'vazio', rotulo: 'nada anotado' },
  { estado: 'ausencia', rotulo: 'ausência' },
  { estado: 'hoje', rotulo: 'hoje' },
  { estado: 'extra', rotulo: 'fim de semana/feriado com hora' },
];
/** Só entra na legenda quando há alguém com rateio fixo na tela. */
export const LEGENDA_RATEIO_FIXO: { estado: EstadoDia; rotulo: string } = { estado: 'presumido', rotulo: 'rateio fixo (não precisa anotar)' };

const AUSENCIAS: Record<string, string> = {
  ferias: 'férias', atestado: 'atestado', folga: 'folga', feriado: 'feriado', licenca: 'licença',
};

/** O que o quadradinho diz quando o mouse para em cima. */
export function textoDoDia(d: DiaPainel, cargaDiaMin: number): string {
  const quando = diaCurto(d.data);
  const onde = [d.remoto ? 'home office' : '', d.campo ? 'campo' : ''].filter(Boolean).join(' e ');
  // Dia sem nada no anotador novo, completado pelo que a pessoa lançou no Clockify.
  const sufixo = `${onde ? ` · ${onde}` : ''}${(d.clockify || 0) > 0 ? ' · anotado no Clockify' : ''}`;
  switch (d.estado) {
    case 'completo': return `${quando} — ${formatarHoras(d.minutos)} anotadas${sufixo}`;
    case 'incompleto':
      return `${quando} — ${formatarHoras(d.minutos)} de ${formatarHoras(cargaDiaMin)} (faltam ${formatarHoras(d.falta)})${sufixo}`;
    case 'vazio': return `${quando} — nada anotado (faltam ${formatarHoras(d.falta)})${sufixo}`;
    case 'hoje':
      return `${quando} — hoje: ${d.minutos > 0 ? `${formatarHoras(d.minutos)} até agora` : 'nada anotado ainda'}${sufixo}`;
    case 'ausencia': return `${quando} — ${AUSENCIAS[d.tipo_ausencia || ''] || 'ausência'}`;
    case 'extra': return `${quando} — ${formatarHoras(d.minutos)} em dia sem expediente${sufixo}`;
    case 'presumido':
      return d.minutos > 0
        ? `${quando} — ${formatarHoras(d.minutos)} anotadas + ${formatarHoras(d.presumido || 0)} pelo rateio fixo${sufixo}`
        : `${quando} — ${formatarHoras(d.presumido || 0)} pelo rateio fixo (não precisa anotar)`;
    case 'futuro': return `${quando} — ainda não chegou`;
    default: return `${quando} — sem expediente`;
  }
}

// ── Situação da pessoa ─────────────────────────────────────────────────────
export type Situacao = 'em_dia' | 'falta' | 'atraso' | 'sem_esperado' | 'sem_ficha' | 'rateio_fixo';

/** A régua da cobrança: dia com hora faltando esperando há 3 dias úteis ou mais. */
export const DIAS_UTEIS_ATRASO = 3;

/** Em dia = anotou ≥ 90% do esperado NO PERÍODO (o servidor decide: `em_dia`).
 *  Quem fez menos num dia e compensou em outro não deve nada — o dia fica
 *  laranja na faixa, mas a pessoa está em dia. */
export function situacaoDe(
  p: Pick<PessoaPainel, 'sem_ficha' | 'carga_min' | 'em_dia' | 'dias_uteis_em_atraso' | 'rateio_fixo'>,
): Situacao {
  if (p.sem_ficha) return 'sem_ficha';
  // Rateio fixo no cadastro: a pessoa não deve anotação — nem em dia, nem devendo.
  if (p.rateio_fixo && p.rateio_fixo.length) return 'rateio_fixo';
  if (!(p.carga_min > 0) || p.em_dia === null) return 'sem_esperado';
  if (p.em_dia) return 'em_dia';
  return p.dias_uteis_em_atraso >= DIAS_UTEIS_ATRASO ? 'atraso' : 'falta';
}

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** A frase da coluna "Situação". */
export function fraseSituacao(p: PessoaPainel): string {
  const s = situacaoDe(p);
  if (s === 'sem_ficha') return 'Login sem ficha no RH — a hora não vira custo';
  if (s === 'rateio_fixo') return 'Rateio fixo — não precisa anotar';
  if (s === 'sem_esperado') return p.dias_ausencia > 0 ? 'Ausente no período' : 'Sem horas esperadas no período';
  if (s === 'em_dia') return 'Em dia';
  return `Faltam ${formatarHoras(p.devendo_min)}`;
}

/** A segunda linha da "Situação": em quantos dias faltou hora e quando foi (ou
 *  desde quando). Só existe para quem tem hora faltando. */
export function detalheSituacao(p: PessoaPainel): string | null {
  const s = situacaoDe(p);
  if (s === 'rateio_fixo') return textoDoRateio(p.rateio_fixo);
  if ((s !== 'falta' && s !== 'atraso') || !p.mais_antigo) return null;
  return p.dias_incompletos === 1
    ? `1 dia incompleto · ${diaCurto(p.mais_antigo)}`
    : `${p.dias_incompletos} dias incompletos · desde ${diaCurto(p.mais_antigo)}`;
}

/** "70% Comercial · 30% Administrativo" — para onde vão as horas de quem tem rateio fixo. */
export function textoDoRateio(rateio: PessoaPainel['rateio_fixo']): string | null {
  if (!rateio || !rateio.length) return null;
  return rateio.map(r => `${String(r.percentual).replace('.', ',')}% ${r.rotulo}`).join(' · ');
}

// ── Filtro e ordem ─────────────────────────────────────────────────────────
export const SEM_SETOR = '(sem setor)';
export const SEM_CHEFE = '(sem chefe)';

export type Filtro = { busca: string; setor: string | null; chefe: string | null; soComFalta: boolean };
export const FILTRO_VAZIO: Filtro = { busca: '', setor: null, chefe: null, soComFalta: false };

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function filtrarPessoas(pessoas: PessoaPainel[], f: Filtro): PessoaPainel[] {
  const busca = semAcento(f.busca.trim());
  return pessoas.filter(p => {
    if (busca && !semAcento(`${p.nome} ${p.usuario_email}`).includes(busca)) return false;
    if (f.setor && (p.setor || SEM_SETOR) !== f.setor) return false;
    if (f.chefe && (p.chefe_nome || SEM_CHEFE) !== f.chefe) return false;
    if (f.soComFalta && p.em_dia !== false) return false;
    return true;
  });
}

/** Opções de um filtro, só com o que existe na equipe. O "(sem …)" vai por
 *  último e só aparece quando há alguém nessa condição. */
export function opcoesDe(pessoas: PessoaPainel[], campo: 'setor' | 'chefe_nome'): Array<{ value: string; label: string }> {
  const vazio = campo === 'setor' ? SEM_SETOR : SEM_CHEFE;
  const nomes = new Set<string>();
  let temVazio = false;
  for (const p of pessoas) {
    if (p.sem_ficha) continue;
    const v = p[campo];
    if (v) nomes.add(v); else temVazio = true;
  }
  const lista = Array.from(nomes).sort((a, b) => a.localeCompare(b, 'pt-BR')).map(n => ({ value: n, label: n }));
  return temVazio ? [...lista, { value: vazio, label: vazio }] : lista;
}

/** Quanto a pessoa deve PARA A TELA: zero para quem está em dia (ou não tinha
 *  hora esperada), mesmo que falte um pouco para os 100%. */
export const devendoDe = (p: Pick<PessoaPainel, 'em_dia' | 'devendo_min'>) => (p.em_dia === false ? p.devendo_min : 0);

/** Ordem de quem precisa de atenção primeiro: quem deve mais hora no topo;
 *  empate pelo nome. */
export function ordenarPorAtencao(pessoas: PessoaPainel[]): PessoaPainel[] {
  return [...pessoas].sort((a, b) => (devendoDe(b) - devendoDe(a)) || a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** Totais do que está NA TELA (depois do filtro) — os cartões acompanham o filtro. */
export function resumoDe(pessoas: PessoaPainel[]): ResumoPainel {
  const soma = (c: keyof PessoaPainel) => pessoas.reduce((s, p) => s + (Number(p[c]) || 0), 0);
  const comCarga = pessoas.filter(p => p.carga_min > 0);
  const devendo = comCarga.filter(p => p.em_dia === false);
  const carga = soma('carga_min');
  const cobertos = comCarga.reduce((s, p) => s + p.minutos_ate_ontem, 0);
  return {
    pessoas: pessoas.length,
    minutos: soma('minutos'), minutos_ate_ontem: soma('minutos_ate_ontem'), minutos_hoje: soma('minutos_hoje'),
    minutos_contrato: soma('minutos_contrato'), minutos_clockify: soma('minutos_clockify'),
    minutos_presumidos: soma('minutos_presumidos'),
    pessoas_rateio_fixo: pessoas.filter(p => p.rateio_fixo && p.rateio_fixo.length).length,
    carga_min: carga, falta_min: soma('falta_min'),
    devendo_min: devendo.reduce((s, p) => s + p.devendo_min, 0),
    minutos_cobertos: cobertos,
    cobertura_pct: carga > 0 ? Math.round((cobertos / carga) * 1000) / 10 : null,
    pessoas_com_carga: comCarga.length,
    pessoas_em_dia: comCarga.filter(p => p.em_dia === true).length,
    pessoas_com_falta: devendo.length,
    pessoas_em_atraso: devendo.filter(p => p.dias_uteis_em_atraso >= DIAS_UTEIS_ATRASO).length,
    pessoas_sem_ficha: pessoas.filter(p => p.sem_ficha).length,
  };
}

/** Chave de uma linha de "para onde foram as horas" (o servidor manda `chave`;
 *  o cálculo aqui é só para resposta antiga em cache). */
export const chaveDoDestino = (d: Pick<DestinoPainel, 'chave' | 'contrato_id' | 'centro_custo_codigo'>) =>
  d.chave || (d.contrato_id ? `c:${d.contrato_id}` : `cc:${d.centro_custo_codigo}`);

/** Listras por cima da cor do dia: é como a faixa mostra que a hora veio do Clockify. */
export const LISTRAS_CLOCKIFY =
  'repeating-linear-gradient(135deg, rgba(255,255,255,0.65) 0, rgba(255,255,255,0.65) 2px, transparent 2px, transparent 5px)';

/** Percentual das horas anotadas que foi para contrato (o resto é interno). */
export function pctContrato(minutos: number, minutosContrato: number): number | null {
  return minutos > 0 ? Math.round((minutosContrato / minutos) * 100) : null;
}

/** "Para onde foram as horas" só de quem está na tela (depois do filtro):
 *  refaz minutos e número de pessoas de cada destino e tira o que zerou. */
export function destinosDe(destinos: DestinoPainel[], pessoas: Array<{ usuario_email: string }>): DestinoPainel[] {
  const visiveis = new Set(pessoas.map(p => p.usuario_email));
  return destinos
    .map(d => {
      const dentro = (d.por_pessoa || []).filter(x => visiveis.has(x.usuario_email));
      return { ...d, por_pessoa: dentro, minutos: dentro.reduce((t, x) => t + x.minutos, 0), pessoas: dentro.length };
    })
    .filter(d => d.minutos > 0)
    .sort((a, b) => (b.minutos - a.minutos) || a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
}
